/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

// export data column description mapping
const columnSchema = {
  'SD-1': [
    {
      'First TextLabel': 'Label indicating the time comparison period selected',
    },
    {
      totalRevenue_agg_comparison:
        'Total reservation revenue observed during the comparison period',
    },
    {
      REVPAR:
        'The reservation revenue per available room, only available when the PMS is toggled on',
    },
    {
      totalRevenue_spot_agg:
        'Total reservation revenue observed for the selected target date period',
    },
    {
      Currency: 'The currency for which the total revenue is shown in the visual',
      Selection: 'The total reservation revenue for the segment selected in the selected period',
      Comparison:
        'The total reservation revenue observed during the comparison period for the segment selected',
    },
    {
      Rooms_spot_agg:
        'The total number of occupied rooms observed for the selected target date period',
    },
    {
      OccupancyRate_agg:
        'The occupancy rate observed for the selected target date period, only available when the PMS toggle is turned on',
    },
    {
      Rooms_agg_comparison: 'Total number of occupied rooms observed during the comparison period',
    },
    {
      Selection:
        'The total number of occupied rooms for the segment selected in the selected period',
      Comparison:
        'The total reservation revenue observed during the comparison period for the segment selected',
    },
    {
      'First TextLabel': 'Label indicating the time comparison period selected',
    },
    {
      ADR_spot_agg: 'The average daily rate of a room observed during the selection period',
    },
    {
      ADR_agg_comparison: 'The average daily rate of a room observed during the comparison period',
    },
    {
      'First TextLabel': 'Label indicating the time comparison period selected',
    },
    {
      Currency: 'The currency for which the average daily rate is calculated for the visual',
      Selection: 'The average daily rate for the segment selected in the selected period',
      Comparison:
        'The average daily rate observed during the comparison period for the segment selected',
    },
    {
      totalRevenue_perc_change_agg:
        'Percentage change in revenue between the selected period and the comparison period',
    },
    {
      Rooms_perc_change_agg:
        'Percentage change in number of occupied rooms between the selected period and the comparison period',
    },
    {
      ADR_perc_change_agg:
        'Percentage change in average daily rate between the selected period and the comparison period',
    },
    {
      Revenue_title: '',
    },
    {
      ADR_title: '',
    },
  ],
  'SD-2': [
    {
      'First TextLabel': 'Label indicating the time comparison period selected',
    },
    {
      TrxCount_metric_comparison:
        'Total number of cancelled reservations observed during the comparison period',
    },
    {
      TrxCount_spot_metric:
        'Total number of cancelled reservations observed for the selected target date period',
    },
    {
      Selection:
        'The total number of cancelled reservations for the segment selected in the selected period',
      Comparison:
        'The total number of cancelled reservations observed during the comparison period for the segment selected',
    },
    {
      TrxCount_spot_metric:
        'Total number of confirmed reservations observed for the selected target date period',
    },
    {
      TrxCount_metric_comparison:
        'Total number of confirmed reservations observed during the comparison period',
    },
    {
      'First TextLabel': 'Label indicating the time comparison period selected',
    },
    {
      Selection:
        'The total number of confirmed reservations for the segment selected in the selected period',
      Comparison:
        'The total number of confirmed reservations observed during the comparison period for the segment selected',
    },
    {
      Currency: 'The currency for which the total revenue is shown in the visual',
      'Total Revenue':
        'Total reservation revenue observed for the selected target date period broken down by the selected segment',
    },
    {
      bookingWindow_otb:
        'Average booking window (gap between the target date and the booking date) for the selected period considering all the booking dates (On-The-Books)',
    },
    {
      LoS_otb:
        'Average Length of Stay for the selected period considering all the booking dates (On-The-Books)',
    },
    {
      TrxCount_perc_change_metric:
        'Percentage change in number confirmed reservations observed between the selected period and the comparison period',
    },
    {
      TrxCount_perc_change_metric:
        'Percentage change in number cancelled reservations observed between the selected period and the comparison period',
    },
  ],
  'SD-3': [
    {
      'Booking Date': 'The particular booking date for the selected target date',
      Sunday:
        "Indicates if the particular booking date is a Sunday or not, 1 indicates it is a Sunday and 0 indicates it's not",
      'Forecasts - Cumulative Total':
        'The cumulative forecast observed on that particular booking date for the selected target date',
      'Comparison*':
        'The cumulative number of rooms occupied or total revenue (metric depends on the pace variable filter) observed for the comparison booking date leading up to the maximum target date selected',
      'Occupied Rooms':
        'The cumulative number of rooms occupied observed for the booking date leading up to the maximum target date selected',
      Revenue:
        'The cumulative amount of reservation revenue observed for the booking date leading up to the maximum target date selected',
      date_considered: '',
      adjusted_cumulative_forecast_lower:
        'The cumulative lower bound forecast observed on that particular booking date for the selected target date',
      adjusted_cumulative_forecast_upper:
        'The cumulative upper bound forecast observed on that particular booking date for the selected target date',
    },
    {
      'Value at Max Stay Date':
        'Shows the total cumulative forecast for the max target date selected',
    },
    {
      Upper: 'Shows the total cumulative upper bound forecast for the max target date selected',
    },
    {
      Lower: 'Shows the total cumulative lower bound forecast for the max target date selected',
    },
    {
      pace_card_label:
        'A label indicating the target date for which the total cumulative forecast value is applicable for',
    },
  ],
  'Regional Breakdown Graphical': [],
  'RBT-1': [
    {
      Continent: 'The continent from where the customers reservation originated from',
      Country: 'The country from where the customers reservation originated from',
      State: 'The state from where the customers reservation originated from',
      City: 'The city from where the customers reservation originated from',
      'Channel (raw)':
        'Specifies the channels that had brought in reservations for the continents, country, state and cities being observed',
      Revenue:
        'Indicates the total reservation revenue brought by the specific channel and region specified',
      'Room Count':
        'Indicates the total occupied rooms booked by the specific channel and region specified',
      ADR: 'Indicates the average daily rate booked by the specific channel and region specified',
    },
  ],
  'PD-1': [
    {
      '': 'The metric for which the Selection, Comparison, % Chg, Selection YTD and Previous Year YTD are derived',
      Selection: 'The total metric value observed for the selected period',
      Comparison: 'The total metric value observed for the comparison period',
      '% Chg':
        'Percentage change in metric observed between the selected period and the comparison period',
      'Selection YTD': 'The total metric value observed for the selection year to date period',
      'Previous Year YTD': 'The total metric value observed for the comparison year to date period',
    },
    {
      'Month Year': 'The target Month year value',
      'Total Room Nights':
        'The total number of room nights observed for the particular target Month Year',
      'Total Revenue': 'The total amount of Revenue observed for the particular target Month Year',
    },
    {
      'Total Room Nights': 'The total number of room nights observed for the selected channel',
      channel: 'Channel for which the reservations was brought through',
    },
    {
      Value: 'The rate category for which the reservation was recorded for',
      'Total Room Nights':
        'The total number of room nights observed for the selected rate category',
    },
    {
      Header_comparison_stay: 'Label indicating the comparison target date period',
    },
    {
      Header_comp_period_bd: 'Label indicating the comparison booking period',
    },
    {
      Header_comp: 'Label indicating the time comparison period selected',
    },
    {
      Header_bd: 'Label indicating the selected booking period',
    },
    {
      Header_pms: 'Label indicating if PMS transactions are included or not',
    },
    {
      Header_currency:
        'Label indicating the currency for which revenue and average daily rate are displayed',
    },
    {
      selected_currency:
        'A separate label indicating the currency for which revenue and average daily rate are displayed',
    },
    {
      Header_sd: 'Label indicating the comparison target date period',
    },
  ],
  'PD-3': [
    {
      'Rate Category':
        'The specific rate category of the reservations observed during the selected period',
      'CP RN': 'The room nights observed during the selected period for the specific rate category',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selected period and the comparison period',
      'CP Rev':
        'The total amount of reservation revenue observed during the selected period for the specific rate category',
      ADR: 'The average daily rate observed during the selected period for the specific rate category',
      LoS: 'The average length of stay observed during the selected period for the specific rate category',
    },
    {
      Channel: 'The specific channels of the reservations observed during the selected period',
      'CP RN': 'The room nights observed during the selected period for the specific channel',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selected period and the comparison period',
      'CP Rev':
        'The total amount of reservation revenue observed during the selected period for the specific channel',
      ADR: 'The average daily rate observed during the selected period for the specific channel',
      ALT: 'The average booking window observed during the selected period for the specific channel',
    },
    {
      'Rate Plan': 'The top 10 rate plans that had brought in reservations for the selected period',
      'CP RN': 'The room nights observed during the selected period for the specific rate plan',
      '% of': 'Percentage of total reservation revenue associated with a specific rate plan',
      'CP Rev':
        'The total amount of reservation revenue observed during the selected period for the specific rate plan',
      ADR: 'The average daily rate observed during the selected period for the specific rate plan',
      'Avg LoS':
        'The average length of stay observed during the selected period for the specific rate plan',
    },
    {
      'Agency Name':
        'The top 10 travel agencies that had brought in reservations for the selected period',
      'CP RN': 'The room nights observed during the selected period for the specific travel agency',
      'LP RN':
        'The room nights observed during the comparison period for the specific travel agency',
      Var: 'The difference between the room nights observed between the selection and the comparison period',
      'CP Rev':
        'The total amount of reservation revenue observed during the selected period for the specific travel agency',
      'LP Rev':
        'The total amount of reservation revenue observed during the comparison period for the specific travel agency',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selected period and the comparison period',
    },
    {
      'Rate Plan':
        'The top 10 rate plans that had grown the most since the user selected time comparison period',
      'CP RN': 'The room nights observed during the selected period for the specific rate plan',
      'LP RN': 'The room nights observed during the comparison period for the specific rate plan',
      'Var.':
        'The difference between the room nights observed during the selection and the comparison period',
      'CP Rev':
        'The total amount of reservation revenue observed during the selected period for the specific rate plan',
      'LP Rev':
        'The total amount of reservation revenue observed during the comparison period for the specific rate plan',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selected period and the comparison period',
    },
    {
      'Rate Plan':
        'The worst 10 rate plans that had performed the worst since the user selected time comparison period',
      'CP RN': 'The room nights observed during the selected period for the specific rate plan',
      'LP RN': 'The room nights observed during the comparison period for the specific rate plan',
      'Var.':
        'The difference between the room nights observed during the selection and the comparison period',
      'CP Rev':
        'The total amount of reservation revenue observed during the selected period for the specific rate plan',
      'LP Rev':
        'The total amount of reservation revenue observed during the comparison period for the specific rate plan',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selected period and the comparison period',
    },
  ],
  'PD-2': [
    {
      Channel:
        'The specific channels for which reservations were observed for during the max target date month and the next 2 months',
      'This Year':
        'Indicates the amount of room nights observed for the third month shown broken down by channel',
      'Last Year':
        'Indicates the amount of room nights observed for the third month comparison period broken down by channel limiting the bookings to a same time last period basis',
      YOY: 'Shows the Year over Year room nights performance of the particular channel for the third month',
      'LY Month End':
        'Indicates the amount of room nights observed for the third month comparison period without limiting the booking dates',
      'TY % MTD OTB':
        "Percentage change in number of room nights observed between the 'This Year' and the 'LY Month End' third month metrics",
    },
    {
      firstMonth_title: 'Indicates the first month for which the pace table metrics are calculated',
    },
    {
      secondMonth_title:
        'Indicates the second month for which the pace table metrics are calculated',
    },
    {
      thirdMonth_title: 'Indicates the third month for which the pace table metrics are calculated',
    },
    {
      this_year_label: 'A label showing the max booking date for which the metrics are calculated',
    },
    {
      last_year_label:
        'A label showing the max comparison booking date for which the metrics are calculated',
    },
    {
      this_year_label: 'A label showing the max booking date for which the metrics are calculated',
    },
    {
      last_year_label:
        'A label showing the max comparison booking date for which the metrics are calculated',
    },
    {
      this_year_label: 'A label showing the max booking date for which the metrics are calculated',
    },
    {
      last_year_label:
        'A label showing the max comparison booking date for which the metrics are calculated',
    },
  ],
  Consortia: {
    consortia: 'Indicates the Consortias that had brought in bookings for the selected period',
    room_nights_cp: 'Indicates the number of room nights observed for the selected period',
    room_nights_lp:
      'Indicates the number of room nights observed during the time comparison period selected',
    room_nights_percentage_change:
      'Indicates the percentage difference between the number of room nights observed in the selected and comparison period',
    cp_rsv: 'Indicates the number of confirmed reservations observed during the selected period',
    lp_rsv:
      'Indicates the number of confirmed reservations observed during the time comparison period selected',
    reservation_percentage_change:
      'Indicates the percentage difference between the number of confirmed reservations observed in the selected and comparison period',
    total_revenue_cp: 'Indicates the total reservation revenue observed for the selected period',
    total_revenue_lp:
      'Indicates the total reservation revenue observed during the time comparison period selected',
    total_revenue_percentage_change:
      'Indicates the percentage difference between the total reservation revenue observed in the selected and comparison period',
    ADR_cp: 'Indicates the average daily rate observed for the selected period',
    ADR_lp: 'Indicates the average daily rate observed during the time comparison period selected',
    ADR_percentage_change:
      'Indicates the percentage difference between the average daily rate observed in the selected and comparison period',
    ALOS_cp: 'Indicates the average length of stay for the selected period',
    ALOS_lp: 'Indicates the average length of stay for the time comparison period selected',
    LeadTime_cp: 'Indicates the average booking window for the selected period',
    LeadTime_lp: 'Indicates the average booking window for the time comparison period selected',
  },
  'Travel Agency': {
    travel_agency_name:
      'Indicates the travel agencies that had brought in bookings for the selected period',
    room_nights_cp: 'Indicates the number of room nights observed for the selected period',
    room_nights_lp:
      'Indicates the number of room nights observed during the time comparison period selected',
    room_nights_percentage_change:
      'Indicates the percentage difference between the number of room nights observed in the selected and comparison period',
    cp_rsv: 'Indicates the number of confirmed reservations observed during the selected period',
    lp_rsv:
      'Indicates the number of confirmed reservations observed during the time comparison period selected',
    reservation_percentage_change:
      'Indicates the percentage difference between the number of confirmed reservations observed in the selected and comparison period',
    total_revenue_cp: 'Indicates the total reservation revenue observed for the selected period',
    total_revenue_lp:
      'Indicates the total reservation revenue observed during the time comparison period selected',
    total_revenue_percentage_change:
      'Indicates the percentage difference between the total reservation revenue observed in the selected and comparison period',
    ADR_cp: 'Indicates the average daily rate observed for the selected period',
    ADR_lp: 'Indicates the average daily rate observed during the time comparison period selected',
    ADR_percentage_change:
      'Indicates the percentage difference between the average daily rate observed in the selected and comparison period',
    ALOS_cp: 'Indicates the average length of stay for the selected period',
    ALOS_lp: 'Indicates the average length of stay for the time comparison period selected',
    LeadTime_cp: 'Indicates the average booking window for the selected period',
    LeadTime_lp: 'Indicates the average booking window for the time comparison period selected',
  },
  'Rate Plan': {
    Rate_code: 'The specific rate category code the reservation was made in',
    Rate_type_name: 'A descriptive name of the rate category code that was used',
    room_nights_cp: 'Indicates the number of room nights observed for the selected period',
    room_nights_lp:
      'Indicates the number of room nights observed during the time comparison period selected',
    room_nights_percentage_change:
      'Indicates the percentage difference between the number of room nights observed in the selected and comparison period',
    cp_rsv: 'Indicates the number of confirmed reservations observed during the selected period',
    lp_rsv:
      'Indicates the number of confirmed reservations observed during the time comparison period selected',
    reservation_percentage_change:
      'Indicates the percentage difference between the number of confirmed reservations observed in the selected and comparison period',
    total_revenue_cp: 'Indicates the total reservation revenue observed for the selected period',
    total_revenue_lp:
      'Indicates the total reservation revenue observed during the time comparison period selected',
    total_revenue_percentage_change:
      'Indicates the percentage difference between the total reservation revenue observed in the selected and comparison period',
    ADR_cp: 'Indicates the average daily rate observed for the selected period',
    ADR_lp: 'Indicates the average daily rate observed during the time comparison period selected',
    ADR_percentage_change:
      'Indicates the percentage difference between the average daily rate observed in the selected and comparison period',
    ALOS_cp: 'Indicates the average length of stay for the selected period',
    ALOS_lp: 'Indicates the average length of stay for the time comparison period selected',
    LeadTime_cp: 'Indicates the average booking window for the selected period',
    LeadTime_lp: 'Indicates the average booking window for the time comparison period selected',
  },
  'Room Nights Pace By Channel Monthly Breakdown': {
    year: 'The specific year being analysed',
    month:
      'The 12 months following and including the month of the maximum selected stay/arrival date',
    channel: 'The channels which had brought in bookings for the specified year and month',
    this_year_rooms: 'Indicates the amount of room nights observed for the year, month and channel',
    last_year_rooms:
      'Indicates teh amount of room nights observed for the year, month and channel limiting the the bookings to a same time last period basis',
    YOY_rooms: 'Shows the Year over Year room nights performance for the year, month and channel',
    last_year_month_end_rooms:
      'Indicates the amount of room nights observed for the comparison period year without limiting the booking dates',
    TY_MTD_OTB:
      "Percentage change in number of room nights observed between the 'this_year_rooms' and the 'last_year_rooms' metrics",
  },
  'TVT-1, TVT-2, TVT-3, TVT-4, TVT-5, TVT-7': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Forecast: 'Indicates the forecasted reservation revenue predicted for future stay dates',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection: 'Indicates the total reservation revenue observed for the selected stay date',
      Comparison:
        'Indicates the total reservation revenue observed for the equivalent comparison stay date based on the selected time comparison period',
    },
    {
      card_label_Revenue: 'Label to provide context the max forecasted date shown above it',
    },
    {
      forecast_Revenue_max:
        'Label indicating the date in which the forecasts were created, the label only appears when the toggle is turned on and the period selected is within the 28 days forecast range',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Forecast: 'Indicates the forecasted occupied room count predicted for future stay dates',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection: 'Indicates the total number of occupied rooms observed for the selected stay date',
      Comparison:
        'Indicates the total number of occupied rooms observed for the equivalent comparison stay date based on the selected time comparison period',
    },
    {
      forecast_room_max:
        'Label indicating the date in which the forecasts were created, the label only appears when the toggle is turned on and the period selected is within the 28 days forecast range',
    },
    {
      card_label_Revenue: 'Label to provide context for the max forecasted date shown above it',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection: 'Indicates the average daily rate observed for the selected stay date',
      Comparison:
        'Indicates the average daily rate observed for the equivalent comparison stay date based on the selected time comparison period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Selection:
        'Indicates the average booking window observed for the selected stay date considering all the booking dates (On-The-Books)',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Comparison:
        'Indicates the average booking window observed for the equivalent comparison stay date based on the selected time comparison period considering all the booking dates (On-The-Books)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Selection:
        'Indicates the average length of stay observed for the selected stay date considering all the booking dates (On-The-Books)',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Comparison:
        'Indicates the average length of stay observed for the equivalent comparison stay date based on the selected time comparison period considering all the booking dates (On-The-Books)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection:
        'Indicates the total number of cancelled reservations observed for the selected stay date. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
      Comparison:
        'Indicates the total number of cancelled reservations observed for the equivalent comparison stay date based on the selected time comparison period. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
    },
  ],
  'WTVT-1, WTVT-2, WTVT-3, WTVT-4, WTVT-5, WTVT-6': [
    {
      'Day Name': 'Indicates the day of week shown on the x-axis',
      SUN: 'Indicates whether the day of week is a Sunday',
      Selection:
        'Indicates the total reservation revenue observed for the selected stay day of week',
      Forecast:
        'Indicates the forecasted reservation revenue predicted for future stay dates which fall in the specific day of week',
      Comparison:
        'Indicates the total reservation revenue observed for the equivalent comparison stay day of week based on the selected time comparison period',
    },
    {
      card_label_Revenue: 'Label to provide context for the max forecasted date shown above it',
    },
    {
      forecast_Revenue_max:
        'Label indicating the date in which the forecasts were created, the label only appears when the toggle is turned on and the period selected is within the 28 days forecast range',
    },
    {
      weekly_label: 'Label indicating the range of stay dates represented in the weekly visual',
    },
    {
      'Day Name': 'Indicates the day of week shown on the x-axis',
      SUN: 'Indicates whether the day of week is a Sunday',
      Selection:
        'Indicates the total number of occupied rooms observed for the selected stay day of week',
      Forecast:
        'Indicates the forecasted total number of occupied rooms predicted for future stay dates which fall in the specific day of week',
      Comparison:
        'Indicates the total number of occupied rooms observed for the equivalent comparison stay day of week based on the selected time comparison period',
    },
    {
      forecast_room_max:
        'Label indicating the date in which the forecasts were created, the label only appears when the toggle is turned on and the period selected is within the 28 days forecast range',
    },
    {
      card_label_Revenue: 'Label to provide context for the max forecasted date shown above it',
    },
    {
      weekly_label: 'Label indicating the range of stay dates represented in the weekly visual',
    },
    {
      'Day Name': 'Indicates the day of week shown on the x-axis',
      SUN: 'Indicates whether the day of week is a Sunday',
      Selection: 'Indicates the average daily rate observed for the selected stay day of week',
      Comparison:
        'Indicates the average daily rate observed for the equivalent comparison stay day of week based on the selected time comparison period',
    },
    {
      weekly_label: 'Label indicating the range of stay dates represented in the weekly visual',
    },
    {
      'Day Name': 'Indicates the day of week shown on the x-axis',
      Selection: 'Indicates the average booking window observed for the selected stay day of week',
      SUN: 'Indicates whether the day of week is a Sunday',
      Comparison:
        'Indicates the average booking window observed for the equivalent comparison stay day of week based on the selected time comparison period',
    },
    {
      weekly_label: 'Label indicating the range of stay dates represented in the weekly visual',
    },
    {
      'Day Name': 'Indicates the day of week shown on the x-axis',
      Selection: 'Indicates the average length of stay observed for the selected stay day of week',
      SUN: 'Indicates whether the day of week is a Sunday',
      Comparison:
        'Indicates the average length of stay observed for the equivalent comparison stay day of week based on the selected time comparison period',
    },
    {
      weekly_label: 'Label indicating the range of stay dates represented in the weekly visual',
    },
    {
      'Day Name': 'Indicates the day of week shown on the x-axis',
      SUN: 'Indicates whether the day of week is a Sunday',
      Selection:
        'Indicates the total number of cancelled reservations observed for the selected stay day of week. Addition of the cancellation count across the day-of-week aggregated values does not represent the total cancellation count for the entire period.',
      Comparison:
        'Indicates the total number of cancelled reservations observed for the equivalent comparison stay day of week based on the selected time comparison period. Addition of the cancellation count across the day-of-week aggregated values does not represent the total cancellation count for the entire period.',
    },
    {
      weekly_label: 'Label indicating the range of stay dates represented in the weekly visual',
    },
  ],
  'CTVT-1, CTVT-2, CTVT-4': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection: 'Indicates the cumulative reservation revenue observed for the selected stay date',
      Forecast:
        'Indicates the forecasted cumulative reservation revenue predicted for future stay dates',
      Comparison:
        'Indicates the cumulative reservation revenue observed for the equivalent comparison stay date based on the selected time comparison period',
    },
    {
      card_label_:
        "Label indicating the max stay date that's forecasted, the label only appears when the toggle is turned on and the period selected is within the 28 days forecast range",
    },
    {
      end_forecast_Revenue_sd:
        'Indicates the cumulative forecast at the maximum selected stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection:
        'Indicates the cumulative number of occupied rooms observed for the selected stay date',
      Forecast:
        'Indicates the forecasted cumulative number of occupied rooms predicted for future stay dates',
      Comparison:
        'Indicates the cumulative number of occupied rooms observed for the equivalent comparison stay date based on the selected time comparison period',
    },
    {
      card_label_:
        "Label indicating the max stay date that's forecasted, the label only appears when the toggle is turned on and the period selected is within the 28 days forecast range",
    },
    {
      end_forecast_Revenue_sd:
        'Indicates the cumulative forecast at the maximum selected stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection:
        'Indicates the cumulative number of cancelled reservations observed for the selected stay date',
      Comparison:
        'Indicates the cumulative number of cancelled reservations observed for the equivalent comparison stay date based on the selected time comparison period',
    },
  ],
  'TVTMA-1, TVTMA-2, TVTMA-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection:
        'Indicates the reservation revenue averaged over the past n days (depending on the moving average filter) observed for the selected stay date',
      Comparison:
        'Indicates the reservation revenue averaged over the past n days (depending on the moving average filter) observed for the equivalent comparison stay date based on the selected time comparison period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection:
        'Indicates the number of occupied rooms averaged over the past n days (depending on the moving average filter) observed for the selected stay date',
      Comparison:
        'Indicates the number of occupied rooms averaged over the past n days (depending on the moving average filter) observed for the equivalent comparison stay date based on the selected time comparison period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      Selection:
        'Indicates the average daily rate averaged over the past n days (depending on the moving average filter) observed for the selected stay date',
      Comparison:
        'Indicates the average daily rate averaged over the past n days (depending on the moving average filter) observed for the equivalent comparison stay date based on the selected time comparison period',
    },
  ],
  'TVTP-1, TVTP-2, TVTP-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      '': 'Line to indicate the 0% base line',
      Selection:
        'Indicates the percentage difference between the first moving average value observed in the stay date range and the moving average values observed for the selected stay date',
      Comparison:
        'Indicates the percentage difference between the first moving average value observed in the comparison stay date range and the moving average values observed for the equivalent comparison stay date based on the selected time comparison period',
      isMaxSelection:
        'A measure used internally in Power BI to indicate the last value in the selected stay date range and display the specific Selection and Comparison percentage value for the stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      '': 'Line to indicate the 0 base line for occupied rooms variance',
      Selection:
        'Indicates the difference in the number of occupied rooms between the first moving average value observed in the stay date range and the moving average values observed for the selected stay date',
      Comparison:
        'Indicates the difference in the number of occupied rooms between the first moving average value observed in the comparison stay date range and the moving average values observed for the equivalent comparison stay date based on the selected time comparison period',
      isMaxSelection:
        'A measure used internally in Power BI to indicate the last value in the selected stay date range and display the specific Selection and Comparison occupied rooms value for the stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      SUN: 'Indicates whether the stay date falls on a Sunday',
      '': 'Line to indicate the 0 base line for ADR variance',
      Selection:
        'Indicates the difference in average daily rate between the first moving average value observed in the stay date range and the moving average values observed for the selected stay date',
      Comparison:
        'Indicates the difference in average daily rate between the first moving average value observed in the comparison stay date range and the moving average values observed for the equivalent comparison stay date based on the selected time comparison period',
      isMaxSelection:
        'A measure used internally in Power BI to indicate the last value in the selected stay date range and display the specific Selection and Comparison ADR value for the stay date',
    },
  ],
  'Trend Time Daily Tabular View': {
    StayDate: 'Indicates the stay date associated with the performance metrics shown in the row',
    room_nights_selected:
      'Indicates the total number of occupied room nights for the selected stay date period',
    room_nights_comparison:
      'Indicates the total number of occupied room nights for the equivalent comparison stay date period',
    room_nights_percentage_change:
      'Indicates the percentage change in occupied room nights between the selected stay date period and the comparison stay date period',
    total_revenue_selected:
      'Indicates the total revenue generated for the selected stay date period',
    total_revenue_comparison:
      'Indicates the total revenue generated for the equivalent comparison stay date period',
    total_revenue_percentage_change:
      'Indicates the percentage change in total revenue between the selected stay date period and the comparison stay date period',
    ADR_selected: 'Indicates the Average Daily Rate (ADR) for the selected stay date period',
    ADR_comparison:
      'Indicates the Average Daily Rate (ADR) for the equivalent comparison stay date period',
    ADR_percentage_change:
      'Indicates the percentage change in Average Daily Rate (ADR) between the selected stay date period and the comparison stay date period',
    ALOS_selected:
      'Indicates the Average Length of Stay (ALOS) for bookings in the selected stay date period',
    ALOS_comparison:
      'Indicates the Average Length of Stay (ALOS) for bookings in the equivalent comparison stay date period',
    ALOS_percentage_change:
      'Indicates the percentage change in Average Length of Stay (ALOS) between the selected stay date period and the comparison stay date period',
    LeadTime_selected:
      'Indicates the average booking lead time, in days, for bookings in the selected stay date period',
    LeadTime_comparison:
      'Indicates the average booking lead time, in days, for bookings in the equivalent comparison stay date period',
    LeadTime_percentage_change:
      'Indicates the percentage change in average booking lead time between the selected stay date period and the comparison stay date period',
    cancellation_selected:
      'Indicates the total number of cancellations recorded for the selected stay date period',
    cancellation_comparison:
      'Indicates the total number of cancellations recorded for the equivalent comparison stay date period',
    cancellation_percentage_change:
      'Indicates the percentage change in cancellations between the selected stay date period and the comparison stay date period',
  },
  'Trend Time Weekly Tabular View': {
    day_name:
      'Indicates the day of the week associated with the aggregated performance metrics shown in the row, or Total for the full selected period',
    room_nights_selected:
      'Indicates the total number of occupied room nights for the selected period and day of week',
    total_revenue_selected:
      'Indicates the total revenue generated for the selected period and day of week',
    ADR_selected: 'Indicates the Average Daily Rate (ADR) for the selected period and day of week',
    ALOS_selected:
      'Indicates the Average Length of Stay (ALOS) for bookings in the selected period and day of week',
    LeadTime_selected:
      'Indicates the average booking lead time, in days, for bookings in the selected period and day of week',
    cancellation_selected:
      'Indicates the total number of cancellations recorded for the selected period and day of week',
  },
  'Trend Time Cumulative Tabular View': {
    StayDate:
      'Indicates the stay date associated with the cumulative performance metrics shown in the row',
    cum_room_nights_selected:
      'Indicates the cumulative total number of occupied room nights up to and including the selected stay date period',
    cum_forecast_rooms:
      'Indicates the cumulative forecasted number of occupied room nights for the selected stay date period',
    cum_total_revenue_selected:
      'Indicates the cumulative total revenue generated up to and including the selected stay date period',
    cum_forecast_revenue:
      'Indicates the cumulative forecasted total revenue for the selected stay date period',
    cum_cancellation_selected:
      'Indicates the cumulative total number of cancellations recorded up to and including the selected stay date period',
  },
  'Trend Time Moving Average Tabular View': {
    StayDate:
      'Indicates the stay date associated with the moving average performance metrics shown in the row',
    room_nights_selected:
      'Indicates the moving average number of occupied room nights for the selected stay date period',
    total_revenue_selected:
      'Indicates the moving average total revenue generated for the selected stay date period',
    ADR_selected:
      'Indicates the moving average Average Daily Rate (ADR) for the selected stay date period',
  },
  'Trend Time Percentage Tabular View': {
    StayDate:
      'Indicates the stay date associated with the moving average percentage change metrics shown in the row',
    pct_change_rn_selected:
      'Indicates the percentage change in moving average occupied room nights compared to the first value in range',
    pct_change_tr_selected:
      'Indicates the percentage change in moving average total revenue compared to the first value in range',
    pct_change_adr_selected:
      'Indicates the percentage change in moving average Average Daily Rate (ADR) compared to the first value in range',
  },
  'TVS-1, TVS-2, TVS-3, TVS-4, TVS-5, TVS-6': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric: 'Indicates the total revenue generated for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric: 'Indicates the total number of occupied rooms for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric: 'Indicates the Average Daily Rate (ADR) for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the average booking window, in days, for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the total number of cancelled reservations for the selected segment and stay date. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'WTVS-1': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric: 'Indicates the total revenue generated for the selected segment and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVS-2': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the total number of occupied rooms for the selected segment and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVS-3': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric: 'Indicates the Average Daily Rate (ADR) for the selected segment and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVS-4': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the average booking window, in days, for the selected segment and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVS-5': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric: 'Indicates the average length of stay for the selected segment and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVS-6': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the total number of cancelled reservations for the selected segment and day of week. Addition of the cancellation count across the day-of-week aggregated values does not represent the total cancellation count for the entire period.',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'CTVS-1, CTVS-2, CTVS-4': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the cumulative total revenue generated up to and including the selected stay date for the selected segment',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },

    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the cumulative total number of occupied rooms up to and including the selected stay date for the selected segment',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the cumulative total number of cancelled reservations up to and including the selected stay date for the selected segment',
    },
  ],
  'TVSMA-1, TVSMA-2, TVSMA-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      dynamic_date_Revenue_MA:
        'Indicates the moving average of total revenue for the selected segment, calculated over the past n days based on the user selected moving average window',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },

    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metrics:
        'Indicates the moving average of occupied room nights for the selected segment, calculated over the past n days based on the user selected moving average window',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the moving average of Average Daily Rate (ADR) for the selected segment, calculated over the past n days based on the user selected moving average window',
    },
  ],
  'TVSP-1, TVSP-2, TVSP-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the percentage change in moving average total revenue for the selected segment compared to the baseline period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis used for weekend highlighting logic',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally to visually highlight weekends in the percentage change chart',
    },
    {
      no_data_message:
        'Visual displayed over the existing visual indicating if no revenue percentage change data is available for the selected filters',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the percentage change in moving average occupied rooms for the selected segment compared to the baseline period',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally to visually highlight weekends in the percentage change chart',
    },
    {
      no_data_message:
        'Visual displayed over the existing visual indicating if no revenue percentage change data is available for the selected filters',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value: 'Indicates the user selected segment category represented by the line in the chart',
      Metric:
        'Indicates the percentage change in moving average Average Daily Rate (ADR) for the selected segment compared to the baseline period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis used for weekend highlighting logic',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally to visually highlight weekends in the percentage change chart',
    },
  ],
  'Trend Segment Daily Tabular View': [
    {
      Date: 'Indicates a stay date from the period selected',
      Metric: 'The name of the metric that is calculated for the particular segment',
      Value: 'Indicates the user selected segment category',
      metrics_spot_2: 'Indicates the metric value for the selected segment and stay date',
    },
    {
      SD: 'Label indicating the stay date range selected',
      BD: 'Label indicating the booking date range selected',
      Header_pms: 'Label indicating if PMS bookings have been filtered out',
      Header_currency: 'Label indicating the currency of the revenue metrics',
    },
  ],
  'Trend Segment Weekly Tabular View': [
    {
      'Day Name': 'Indicates the day of the week shown',
      Metric: 'The name of the metric that is calculated for the particular segment',
      Value: 'Indicates the user selected segment category',
      metrics_spot_2: 'Indicates the metric value for the selected segment and day of week',
    },
    {
      SD: 'Label indicating the stay date range selected',
      BD: 'Label indicating the booking date range selected',
      Header_pms: 'Label indicating if PMS bookings have been filtered out',
      Header_currency: 'Label indicating the currency of the revenue metrics',
    },
  ],
  'Trend Segment Cumulative Tabular View': [
    {
      Date: 'Indicates a stay date from the period selected',
      Metric: 'The name of the metric that is calculated for the particular segment',
      Value: 'Indicates the user selected segment category',
      cumulative_metrics_spot:
        'Indicates the cumulative metric value for the selected segment and stay date',
    },
    {
      SD: 'Label indicating the stay date range selected',
      BD: 'Label indicating the booking date range selected',
      Header_pms: 'Label indicating if PMS bookings have been filtered out',
      Header_currency: 'Label indicating the currency of the revenue metrics',
    },
  ],
  'Trend Segment Moving Average Tabular View': [
    {
      Date: 'Indicates a stay date from the period selected',
      Metric: 'The name of the metric that is calculated for the particular segment',
      Value: 'Indicates the user selected segment category',
      metrics_spot_MA:
        'Indicates the metric averaged over the past n days (depending on the moving average filter) observed for the selected stay date',
    },
    {
      SD: 'Label indicating the stay date range selected',
      BD: 'Label indicating the booking date range selected',
      Header_pms: 'Label indicating if PMS bookings have been filtered out',
      Header_currency: 'Label indicating the currency of the revenue metrics',
    },
  ],
  'Trend Segment Percentage Tabular View': [
    {
      Date: 'Indicates a stay date from the period selected',
      Metric: 'The name of the metric that is calculated for the particular segment',
      Value: 'Indicates the user selected segment category',
      percentage_metrics_spt:
        'Indicates the percentage change in moving average metric for the selected segment compared to the baseline period',
    },
    {
      SD: 'Label indicating the stay date range selected',
      BD: 'Label indicating the booking date range selected',
      Header_pms: 'Label indicating if PMS bookings have been filtered out',
    },
  ],
  'TXS-1, TXS-2, TXS-3, TXS-4, TXS-5, TXS-6': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the reservation revenue generated for the selected segment and stay date, for the time comparison labels the reservation revenue generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the number of occupied rooms generated for the selected segment and stay date, for the time comparison labels the number of occupied rooms generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the average daily rate generated for the selected segment and stay date, for the time comparison labels the average daily rate generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the average length of stay generated for the selected segment and stay date, for the time comparison labels the average length of stay generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the average booking window generated for the selected segment and stay date, for the time comparison labels the average booking window generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the number of cancelled reservations generated for the selected segment and stay date, for the time comparison labels the number of cancelled reservations generated is for the selected time comparison period calculated for the selected segment and stay date. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'WTXST-1': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the reservation revenue generated for the selected segment and stay date, for the time comparison labels the reservation revenue generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week shown on the x-axis',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTXST-2': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the number of occupied rooms generated for the selected segment and stay date, for the time comparison labels the number of occupied rooms generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week shown on the x-axis',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTXST-3': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the number of average daily rate generated for the selected segment and stay date, for the time comparison labels the average daily rate generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week shown on the x-axis',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTXST-4': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the number of average length of stay generated for the selected segment and stay date, for the time comparison labels the average length of stay generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week shown on the x-axis',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTXST-5': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the number of average booking window generated for the selected segment and stay date, for the time comparison labels the average booking window generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week shown on the x-axis',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTXST-6': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the number of cancelled reservations generated for the selected segment and stay date, for the time comparison labels the number of cancelled reservations generated is for the selected time comparison period calculated for the selected segment and stay date. Addition of the cancellation count across the day-of-week aggregated values does not represent the total cancellation count for the entire period.',
    },
    {
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week shown on the x-axis',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],

  'CTXS-1, CTXS-2, CTXS-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention the metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the cumulative reservation revenue generated for the selected segment and stay date, for the time comparison labels the cumulative reservation revenue generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention the metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the cumulative number of occupied rooms generated for the selected segment and stay date, for the time comparison labels the cumulative number of occupied rooms generated is for the selected time comparison period calculated for the selected segment and stay date',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the user selected segment category represented by the line in the chart, the labels mention the metric seen for the selection period and the user selected time comparison period',
      Metric:
        'Indicates the cumulative number of cancelled reservations generated for the selected segment and stay date, for the time comparison labels the cumulative number of cancelled reservations generated is for the selected time comparison period calculated for the selected segment and stay date. The final cumulative value observed for the period represents the total cancellation count for the period.',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'CTTXST-1': [
    {
      Date: 'Indicates a particular stay date from the user selected date period',
      Value: 'Label indicating the specific segment from the user selected breakdown option',
      Metric: 'Label indicating the metric being generated',
      Label:
        'Label indicating the if the metric shown is for the selected period, comparison period or a percentage difference of the selection and comparison period',
      metrics_spot_2_deagg:
        'Indicates the metric values for the selected segment, stay date and label',
    },
    {
      SD: 'Label indicating the stay date range selected',
      BD: 'Label indicating the booking date range selected',
      Header_currency: 'Label indicating the currency of the revenue metrics',
    },
  ],
  'CWTTXST-1': [
    {
      'Day Name': 'Indicates a particular day of week from the user selected stay date period',
      Value: 'Label indicating the specific segment from the user selected breakdown option',
      Metric: 'Label indicating the metric being generated',
      Label:
        'Label indicating the if the metric shown is for the selected period, comparison period or a percentage difference of the selection and comparison period',
      metrics_spot_2_deagg:
        'Indicates the metric values for the selected segment, stay day of week and label',
    },
    {
      SD: 'Label indicating the stay date range selected',
      BD: 'Label indicating the booking date range selected',
      Header_currency: 'Label indicating the currency of the revenue metrics',
    },
  ],
  'CCTTXST-1': [
    {
      Date: 'Indicates a particular stay date from the user selected date period',
      Value: 'Label indicating the specific segment from the user selected breakdown option',
      Metric: 'Label indicating the cumulative metric being generated',
      Label:
        'Label indicating the if the cumulative metric shown is for the selected period, comparison period or a percentage difference of the selection and comparison period',
      metrics_spot_2_deagg:
        'Indicates the cumulative metric values for the selected segment, stay date and label',
    },
    {
      SD: 'Label indicating the stay date range selected',
      BD: 'Label indicating the booking date range selected',
      Header_currency: 'Label indicating the currency of the revenue metrics',
    },
  ],
  'PVT-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      SUN: 'This highlights dates of the week that are Sunday',
      Selection: 'This line shows the pace of revenue for the selected dates',
      Comparison:
        'This line shows the pace of revenue for the selected dates from the comparison period (selected from comparison window)',
      Forecast: 'This line shows the pace of forecast revenue for the selected stay date',
    },
    {
      forecast_Revenue_max:
        'This is a card to show the date on which the forecasts have been calculated on',
    },
    {
      card_label_Revenue:
        "This is a text card to show 'Forecasted on', if forecast toggle is turned on",
    },
  ],
  'PVT-2': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      SUN: 'This highlights dates of the week that are Sunday',
      Comparison:
        'This line shows the pace of room nights booked for the selected dates from the comparison period (selected from comparison window)',
      Selection: 'This line shows the pace of room nights booked for the selected dates',
      Forecast: 'This line shows the pace of forecast room nights for the selected stay date',
    },
    {
      card_label_Revenue:
        "This is a text card to show 'Forecasted on', if forecast toggle is turned on",
    },
  ],
  'PVT-3': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      SUN: 'This highlights dates of the week that are Sunday',
      Selection: 'This line shows the pace of Average Daily Rate for the selected dates',
      Comparison:
        'This line shows the pace of Average Daily Rate for the selected dates from the comparison period (selected from comparison window)',
    },
  ],
  'PVT-4': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      SUN: 'This highlights dates of the week that are Sunday',
      Selection:
        'This line shows the pace of cancellation for the selected dates. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
      Comparison:
        'This line shows the pace of cancellation for the selected dates from the comparison period (selected from comparison window). Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
      show_data: 'This shows dependant on the max and min for the booking window',
      date_considered_nf: 'This shows the max X-Axis line for data points excluding the forecasts',
      date_considered: 'This shows the max X-Axis line for all data points',
      Bar_Color: '#FFFFFF00',
    },
  ],
  'PVTC-1': [
    {
      card_label_Revenue:
        "This is a text card to show 'Forecasted on', if forecast toggle is turned on",
    },
    {
      forecast_metric_max:
        'This is a card to show the date on which the forecasts have been calculated on',
    },
    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      SUN: 'This highlights dates of the week that are Sunday',
      Comparison:
        'This line shows the cumulative pace of revenue for the selected dates from the comparison period (selected from comparison window)',
      Forecast:
        'This line shows the cumulative pace of forecast revenue for the selected stay date',
      '% line':
        'This shows the chosen percentage line, highlighting at which point that percent of cumulative bookings has been reached',
      Revenue: 'This line shows the cumulative pace of revenue for the selected dates',
      adjusted_cumulative_forecast_lower:
        'This shows the lower bound for the available cumulative forecast',
      adjusted_cumulative_forecast_upper:
        'This shows the upper bound for the available cumulative forecast',
      'Occupied Rooms': 'This line shows the cumulative pace of Room nights for the selected dates',
    },
  ],
  'PVTC-2': [
    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      SUN: 'This highlights dates of the week that are Sunday',
      Selection: 'This line shows the cumulative pace of Average Daily Rate for the selected dates',
      Comparison:
        'This line shows the cumulative pace of Average Daily Rate for the selected dates from the comparison period (selected from comparison window)',
      Bar_Color: '#FCEAE6',
    },
  ],
  'PVTC-3': [
    {
      card_label_Revenue:
        "This is a text card to show 'Forecasted on', if forecast toggle is turned on",
    },
    {
      forecast_metric_max:
        'This is a card to show the date on which the forecasts have been calculated on',
    },
    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      SUN: 'This highlights dates of the week that are Sunday',
      Selection: 'This line shows the cumulative pace of Cancellations for the selected dates',
      Comparison:
        'This line shows the cumulative pace of Cancellations for the selected dates from the comparison period (selected from comparison window)',
      date_considered_nf: 'This shows the X-Axis Max line for data excluding the forecasts',
      Bar_Color: '#FFFFFF00',
    },
  ],
  'PVTL-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Forecast: 'This line shows the pace of forecast revenue for the selected stay date',
      Selection: 'This line shows the pace of revenue for the selected dates',
      Comparison:
        'This line shows the pace of revenue for the selected dates from the comparison period (selected from comparison window)',
    },
    {
      forecast_Revenue_max:
        'This is a card to show the date on which the forecasts have been calculated on',
    },
    {
      card_label_Revenue:
        "This is a text card to show 'Forecasted on', if forecast toggle is turned on",
    },
  ],
  'PVTL-2': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Forecast: 'This line shows the pace of forecast room nights for the selected stay date',
      Selection: 'This line shows the pace of room nights for the selected dates',
      Comparison:
        'This line shows the pace of room nights for the selected dates from the comparison period (selected from comparison window)',
    },
    {
      card_label_Revenue:
        "This is a text card to show 'Forecasted on', if forecast toggle is turned on",
    },
  ],
  'PVTL-3': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Selection: 'This line shows the pace of Average Daily Rate for the selected dates',
      Comparison:
        'This line shows the pace of Average Daily Rate for the selected dates from the comparison period (selected from comparison window)',
    },
  ],
  'PVTL-4': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Selection:
        'This line shows the pace of Cancellations for the selected dates. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
      Comparison:
        'This line shows the pace of Cancellations for the selected dates from the comparison period (selected from comparison window). Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
    },
  ],
  'PVTCL-1': [
    {
      card_label_Revenue:
        "This is a text card to show 'Forecasted on', if forecast toggle is turned on",
    },
    {
      forecast_metric_max:
        'This is a card to show the date on which the forecasts have been calculated on',
    },
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Selection:
        'This line shows the cumulative pace of Revenue / Room nights for the selected dates',
      Comparison:
        'This line shows the cumulative pace of Revenue / Room nights for the selected dates from the comparison period (selected from comparison window)',
      Forecast:
        'This line shows the cumulative pace of forecast Revenue / Room nights for the selected stay date',
      '% Line':
        'This shows the chosen percentage line, highlighting at which point that percent of cumulative bookings has been reached',
    },
  ],
  'PVTCL-2': [
    {
      'Booking Window':
        'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Selection: 'These values show the pace of the metric for the selected stay date range',
      Comparison:
        'These values show the pace of the metric for the selected dates from the comparison period (selected from comparison window)',
    },
  ],
  'PVTCL-3': [
    {
      'Booking Window':
        'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Comparison:
        'This line shows the cumulative pace of cancellations for the selected dates from the comparison period (selected from comparison window)',
      Selection: 'This line shows the cumulative pace of cancellations for the selected dates',
    },
    {
      card_label_Revenue:
        "This is a text card to show 'Forecasted on', if forecast toggle is turned on",
    },
    {
      forecast_metric_max:
        'This is a card to show the date on which the forecasts have been calculated on',
    },
  ],
  'CPT-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Selection: 'These values show the pace of the metric for the selected stay date range',
      Comparison:
        'These values show the pace of the metric for the selected dates from the comparison period (selected from comparison window)',
      '% Chg':
        'This shows the percentage change between the selected period and comparison period values',
      Forecast:
        'This shows the pace of forecast if forecast toggle is turned on and if forecast is available',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
    },
    {
      SD: 'This shows the selected stay date range chosen by user, which may be aligned a little for individual use cases',
      BD: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
    },
    {
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],
  'CCPT-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Selection:
        'These values show the cumulative pace of the metric for the selected stay date range',
      Comparison:
        'These values show the cumulative pace of the metric for the selected dates from the comparison period (selected from comparison window)',
      '% Chg':
        'This shows the percentage change between the selected period and comparison period values',
      Forecasts:
        'This shows the cumulativepace of forecast if forecast toggle is turned on and if forecast is available',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
    },
    {
      SD: 'This shows the selected stay date range chosen by user, which may be aligned a little for individual use cases',
      BD: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
    },
    {
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],
  'CPLT-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Selection: 'These values show the pace of the metric for the selected stay date range',
      Comparison:
        'These values show the pace of the metric for the selected dates from the comparison period (selected from comparison window)',
      '% Chg':
        'This shows the percentage change between the selected period and comparison period values',
      Forecasts:
        'This shows the pace of forecast if forecast toggle is turned on and if forecast is available',
    },
    {
      SD: 'This shows the selected stay date range chosen by user',
      BD: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
    },
    {
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],
  'CCPLT-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Selection:
        'These values show the cumulative pace of the metric for the selected stay date range',
      Comparison:
        'These values show the cumulative pace of the metric for the selected dates from the comparison period (selected from comparison window)',
      '% Chg':
        'This shows the percentage change between the selected period and comparison period values',
      Forecast:
        'This shows the cumulative pace of forecast if forecast toggle is turned on and if forecast is available',
    },
    {
      SD: 'This shows the selected stay date range chosen by user',
      BD: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
    },
    {
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],

  'PVS-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric: 'This shows the pace of revenue for the selected stay date for each category',
    },
  ],
  'PVS-2': [
    {
      Date: 'This shows confirmation dates and are shown only till the max stay date or max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric: 'This shows the pace of room nights for the selected stay date for each category',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max stay date or max available confirmation date',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVS-3': [
    {
      Date: 'This shows confirmation dates and are shown only till the max stay date or max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric:
        'This shows the pace of Average Daily Rate for the selected stay date for each category',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max stay date or max available confirmation date',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVS-4': [
    {
      Date: 'This shows confirmation dates and are shown only till the max stay date or max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      dynamic_date_TrxCount_bd:
        'This shows the pace of cancellations for the selected stay date for each category. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max stay date or max available confirmation date',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVSC-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric:
        'This shows the cumulative pace of revenue for the selected stay date for each category',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation date',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVSC-2': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric:
        'This shows the cumulative pace of room nights for the selected stay date for each category',
    },
    {
      Date: '2026-04-24 00:00:00',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVSC-4': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric:
        'This shows the cumulative pace of Average Daily Rate for the selected stay date for each category',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation date',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVSC-3': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation date ',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      cumulative_TrxCount_grouping_bd:
        'This shows the cumulative pace of cancellations for the selected stay date for each category',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation date',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],

  'PVSL-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric: 'This shows the pace of revenue for the selected stay date for each category',
    },
  ],
  'PVSL-2': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric: 'This shows the pace of room nights for the selected stay date for each category',
    },
  ],
  'PVSL-3': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric:
        'This shows the pace of Average Daily Rate for the selected stay date for each category',
    },
  ],
  'PVSL-4': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      dynamic_date_TrxCount_2:
        'This shows the pace of Cancellation for the selected stay date for each category. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
    },
  ],

  'PVSLC-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric:
        'This shows the cumulative pace of revenue for the selected stay date for each category',
    },
  ],
  'PVSLC-2': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric:
        'This shows the cumulative pace of room nights for the selected stay date for each category',
    },
  ],
  'PVSLC-4': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      Metric:
        'This shows the cumulative pace of average daily rate for the selected stay date for each category',
    },
  ],
  'PVSLC-3': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      cumulative_TrxCount_la:
        'This shows the cumulative pace of cancellation for the selected stay date for each category',
    },
  ],
  'CPS-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      metrics_spot: 'This shows the pace of metrics for each category',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pms: 'This shows if the PMS toggle is On/Off',
    },
    {
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],
  'CCPS-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric: 'These show the metrics displayed (Revenue, room nights, ADR, cancellation count)',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      metrics_spot_cumulative: 'This shows the cumulative pace of metrics for each category',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pms: 'This shows if the PMS toggle is On/Off',
    },
    {
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],
  'CPLS-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      metrics_spot_la: 'This shows the pace of metrics for each category',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pms: 'This shows if the PMS toggle is On/Off',
    },
    {
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],
  'CCPLS-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Value:
        'This indicates the category for which the metrics are showing from the chosen breakdown',
      metrics_spot_cumulative_la: 'This shows the cumulative pace of metrics for each category',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pms: 'This shows if the PMS toggle is On/Off',
    },
    {
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],

  'PVTS-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the pace of revenue for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVTS-2': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the pace of room nights for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVTS-3': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the pace of average daily rate for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVTS-4': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the pace of cancellations for the selected stay date for segment breakdown for comparison and selected period. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVTSC-1': [
    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation',
      Time_segment_PaceVariable_cumulative:
        'These show the cumulative pace of revenue/rooms for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],
  'PVTSC-2': [
    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the cumulative pace of Average daily rate for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],
  'PVTSC-3': [
    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the cumulative pace of cancellation for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],
  'PVTSL-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the pace of revenue for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],
  'PVTSL-2': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the pace of room nights for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],
  'PVTSL-3': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the pace of average daily rate for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],
  'PVTSL-4': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the pace of cancellation count for the selected stay date for segment breakdown for comparison and selected period. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],

  'PVTSCL-1': [
    {
      'Booking Window':
        'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the cumulative pace of revenue/rooms for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],
  'PVTSCL-2': [
    {
      'Booking Window':
        'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the cumulative pace of average daily rate for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],
  'PVTSCL-3': [
    {
      'Booking Window':
        'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the cumulative pace of cancellation count for the selected stay date for segment breakdown for comparison and selected period',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison periods',
    },
  ],

  'CPTS-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Value:
        'These show the segment breakdown categories for each of the selected and comparison periods',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Label: 'This indicates if it is the selected period or the comparison period',
      Values:
        'These show the pace of metrics for the selected/comparison stay range, confirmation date and segment breakdown',
    },
    {
      SD: 'This shows the selected stay date range chosen by user',
      BD: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
    },
    {
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],
  'CCPTS-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Value:
        'These show the segment breakdown categories for each of the selected and comparison periods',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Label: 'This indicates if it is the selected period or the comparison period',
      Values:
        'These show the cumulative pace of metrics for the selected/comparison stay range, confirmation date and segment breakdown',
    },
    {
      SD: 'This shows the selected stay date range chosen by user',
      BD: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
    },
    {
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],
  'CPLTS-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'These show the segment breakdown categories for each of the selected and comparison periods',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Label: 'This indicates if it is the selected period or the comparison period',
      Values:
        'These show the pace of metrics for the selected/comparison stay range, confirmation date and segment breakdown',
    },
    {
      SD: 'This shows the selected stay date range chosen by user',
      BD: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
    },
    {
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],
  'CCPLTS-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value:
        'These show the segment breakdown categories for each of the selected and comparison periods',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Label: 'This indicates if it is the selected period or the comparison period',
      Values:
        'These show the cumulative pace of metrics for the selected/comparison stay range, confirmation date and segment breakdown',
    },
    {
      SD: 'This shows the selected stay date range chosen by user',
      BD: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
    },
    {
      Header_currency: 'This displays what the reporting currency on the table is',
    },
  ],

  'PCC-1': [
    {
      card_label_Revenue:
        "This is a text card to show 'Forecasted on', if forecast toggle is turned on",
    },
    {
      forecast_metric_max:
        'This is a card to show the date on which the forecasts have been calculated on',
    },

    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation',
      'Cumulative Net Room Count': 'This shows the cumulative pace of room count',
      status:
        'This indicated if the status of the booking is reserved,in-house,no-show, checked out, confirmed or cancelled',
      'Room Count':
        'This shows the room count for the selected stay date made at each confirmation date',
      is_sunday_min_room_count: 'Used internally to identify days of the week that are sundays',
      is_sunday_max_room_count: 'Used internally to identify days of the week that are sundays',
    },
  ],
  'PVHF-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the pace of revenue for each Hotel',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVHF-2': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      dynamic_date_Rooms_pace_forecast: 'This shows the pace of room nights for each Hotel',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVH-3': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the pace of Average daily rate for each Hotel',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVH-4': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric:
        'This shows the pace of cancellations for each Hotel. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      adjusted_pace_isSunday_2:
        "This is a text card that shows the message :'No results for selected filters' , if selected filter combination does not have data for the visual",
    },
  ],

  'PVHFC-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the cumulative pace of revenue for each Hotel',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVHFC-2': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the cumulative pace of room nights for each Hotel',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVHC-3': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the cumulative pace of Average Daily Rate for each Hotel',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVHC-4': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the cumulative pace of cancellation for each Hotel',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],

  'PVHFL-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the pace of revenue for each Hotel',
    },
  ],
  'PVHFL-2': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the pace of room nights for each Hotel',
    },
  ],
  'PVHL-3': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the pace of Average Daily Rate for each Hotel',
    },
  ],
  'PVHL-4': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric:
        'This shows the pace of cancellations for each Hotel. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
    },
  ],

  'PVHFLC-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the cumulative pace of revenue for each Hotel',
    },
  ],
  'PVHFLC-2': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the cumulative pace of room nights for each Hotel',
    },
  ],
  'PVHLC-3': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the cumulative pace of average daily rate for each Hotel',
    },
  ],
  'PVHLC-4': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Value:
        'This indicates the lines for the selected hotel, comparison hotel, selected forecast hotel and comparison forecast hotel',
      Metric: 'This shows the cumulative pace of cancellations for each Hotel',
    },
  ],
  'CPH-1': [
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays the selected currency',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Label: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      'A Actls': 'These values show the pace of the metric for the selected Hotel',
      'B Actls': 'These values show the pace of the metric for the comparison Hotel',
      '% Chg': 'This shows the percentage change between the Selected and comparison hotel values',
      'A Fcsts':
        'This shows the forecast values for the selected hotel if forecasts are toggled on',
      'B Fcsts':
        'This shows the forecast values for the comparison hotel if forecasts are toggled on',
    },
  ],
  'CCPH-1': [
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays the selected currency',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Label: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      'A Actls': 'These values show the cumulative pace of the metric for the selected Hotel',
      'B Actls': 'These values show the cumulative pace of the metric for the comparison Hotel',
      '% Chg': 'This shows the percentage change between the Selected and comparison hotel values',
      'A Fcsts':
        'This shows the cumulative forecast values for the selected hotel if forecasts are toggled on',
      'B Fcsts':
        'This shows the cumulative forecast values for the comparison hotel if forecasts are toggled on',
    },
  ],
  'CPLH-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Label: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      'A Actls': 'These values show the pace of the metric for the selected Hotel',
      'B Actls': 'These values show the pace of the metric for the comparison Hotel',
      '% Chg': 'This shows the percentage change between the Selected and comparison hotel values',
      'A Fcsts':
        'This shows the forecast values for the selected hotel if forecasts are toggled on',
      'B Fcsts':
        'This shows the forecast values for the comparison hotel if forecasts are toggled on',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays the selected currency',
    },
  ],
  'CCPLH-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date or the max forecasted date if the toggle is turned on AND if forecasts are available',
      Label: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      'A Actls': 'These values show the cumulative pace of the metric for the selected Hotel',
      'B Actls': 'These values show the cumulative pace of the metric for the comparison Hotel',
      '% Chg': 'This shows the percentage change between the Selected and comparison hotel values',
      'A Fcsts':
        'This shows the cumulative pace of forecast values for the selected hotel if forecasts are toggled on',
      'B Fcsts':
        'This shows the cumulative pace of forecast values for the comparison hotel if forecasts are toggled on',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays the selected currency',
    },
  ],

  'PVHS-1': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the pace of revenue for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
  ],
  'PVHS-2': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the pace of room nights for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVHS-3': [
    {
      Dates: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the pace of average daily rate for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVHS-4': [
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Hotel_segment_TrxCount:
        'These show the pace of cancellation for the selected stay date for each individual hotel and segment breakdown. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],

  'PVHSC-1': [
    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the cumulative pace of revenue for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the cumulative pace of room nights for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVHSC-2': [
    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the cumulative pace of average daily rate for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],
  'PVHSC-3': [
    {
      'Booking Date':
        'This shows confirmation dates and are shown only till the max available confirmation',
      Metric:
        'These show the cumulative pace of cancellation for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
    {
      Date: 'This shows confirmation dates and are shown only till the max available confirmation',
      adjusted_pace_isSunday_2: 'This highlights dates of the week that are Sunday',
    },
  ],

  'PVHSL-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the pace of revenue for the selected stay date for each individual hotel and segment breakdown',
      label:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
  ],
  'PVHSL-2': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the pace of room nights for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
  ],
  'PVHSL-3': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the pace of Average Daily Rate for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
  ],
  'PVHSL-4': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the pace of Cancellations for the selected stay date for each individual hotel and segment breakdown. Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period.',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
  ],

  'PVHSCL-1': [
    {
      'Booking Window':
        'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the cumulative pace of revenue/ room nights for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
  ],
  'PVHSCL-2': [
    {
      'Booking Window':
        'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the cumulative pace of average daily rate for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
  ],
  'PVHSCL-3': [
    {
      'Booking Window':
        'This shows the lags from the selected dates to the max available confirmation date',
      Metric:
        'These show the cumulative pace of cancellations for the selected stay date for each individual hotel and segment breakdown',
      Values:
        'These show the segment breakdown categories for each of the selected and comparison hotels',
    },
  ],

  'CPHS-3': [
    {
      Date: '"This shows confirmation dates and are shown only till the max available confirmation"',
      Value: 'These show the segment breakdown categories',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Label: 'These indicate if it is the selected/comparison hotel',
      Values:
        'These show the pace of metrics for the selected stay date for each individual hotel, confirmation date and segment breakdown',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays the selected currency',
    },
  ],
  'CCPHS-4': [
    {
      Date: '"This shows confirmation dates and are shown only till the max available confirmation"',
      Value: 'These show the segment breakdown categories',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Label: 'These indicate if it is the selected/comparison hotel',
      Values:
        'These show the cumulative pace of metrics for the selected stay date for each individual hotel, confirmation date and segment breakdown',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays the selected currency',
    },
  ],
  'CPLHS-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value: 'These show the segment breakdown categories',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Label: 'These indicate if it is the selected/comparison hotel',
      Values:
        'These show the pace of metrics for the selected stay date for each individual hotel, confirmation date and segment breakdown',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays the selected currency',
    },
  ],
  'CCPLHS-1': [
    {
      lags: 'This shows the lags from the selected dates to the max available confirmation date',
      Value: 'These show the segment breakdown categories',
      Metric: 'These show the metrics displayed (Revenue, room nights,ADR, cancellation count)',
      Label: 'These indicate if it is the selected/comparison hotel',
      Values:
        'These show the cumulative pace of metrics for the selected stay date for each individual hotel, confirmation date and segment breakdown',
    },
    {
      Header_sd: 'This shows the selected stay date range chosen by user',
      Header_bd: 'This shows the user selected booking date range or OTB range',
      Header_pace_horizon: 'This shows the horizon range selected by the user',
      Header_currency: 'This displays the selected currency',
    },
  ],
  'PDH-1': [
    {
      '': 'The metric for which the Selection, Comparison, % Chg, Selection YTD and Previous Year YTD are derived',
      Selection: 'The total metric values observed for the selection hotel(s)',
      Comparison: 'The total metric values observed for the comparison hotel(s)',
      '% Chg':
        'Percentage change in metric observed between the selection and comparison hotels year to date period',
      'Selection YTD':
        'The total metric value observed for the selection hotel(s) year to date period',
      'Comparison YTD':
        'The total metric value observed for the comparison hotel(s) year to date period',
      '% Chg  ':
        'Percentage change in metric observed between the selection and comparison hotels year to date period',
    },
    {
      'Month Year': 'The target Month year value',
      'Selection Room Nights':
        'The total number of room nights observed for the particular target Month Year and selection hotel(s)',
      'Comparison Room Nights':
        'The total number of room nights observed for the particular target Month Year and comparison hotel(s)',
      'Selection Revenue':
        'The total revenue observed for the particular target Month Year and selection hotel(s)',
      'Comparison Revenue':
        'The total revenue observed for the particular target Month Year and comparison hotel(s)',
    },
    {
      channel: 'Channel for which the reservations was brought through for the selection hotel(s)',
      'Total Room Nights':
        'The total number of room nights observed for the selected channel and selection hotel(s)',
    },
    {
      Value: 'The rate category for which the reservation was recorded for the selection hotel(s)',
      'Total Room Nights':
        'The total number of room nights observed for the selected rate category and selection hotel(s)',
    },
    {
      selected_currency:
        'A separate label indicating the currency for which revenue and average daily rate are displayed',
    },
  ],
  'PDH-3': [
    {
      'Rate Category':
        'The specific rate category of the reservations observed during the selected period',
      'Sel. RN':
        'The room nights observed during the selected period and selection hotel(s) for the specific rate category',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selected hotel(s) and comparison hotel(s)',
      'Sel. Rev':
        'The total amount of reservation revenue observed during the selected period and selection hotel(s) for the specific rate category',
      ADR: 'The average daily rate observed during the selected period and selection hotel(s) for the specific rate category',
      'Avg LoS':
        'The average length of stay observed during the selected period and selection hotel(s) for the specific rate category',
    },
    {
      Channel: 'The specific channels of the reservations observed during the selected period',
      'Sel. RN':
        'The room nights observed during the selected period and selection hotel(s) for the specific channel',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selection hotel(s) and the comparison hotel(s)',
      'Sel. Rev':
        'The total amount of reservation revenue observed during the selected period and selection hotel(s) for the specific channel',
      ADR: 'The average daily rate observed during the selected period and selection hotel(s) for the specific channel',
      ALT: 'The average booking window observed during the selected period and selection hotel(s) for the specific channel',
    },
    {
      'Rate Plan':
        'The top 10 rate plans that had brought in reservations for the selected period and selection hotel(s)',
      'Sel. RN':
        'The room nights observed during the selected period and selection hotel(s) for the specific rate plan',
      '% of':
        'Percentage of total reservation revenue associated with a specific rate plan for the selection hotel(s)',
      'Sel. Rev':
        'The total amount of reservation revenue observed during the selected period and selection hotel(s) for the specific rate plan',
      ADR: 'The average daily rate observed during the selected period and selection hotel(s) for the specific rate plan',
      'Avg LoS':
        'The average length of stay observed during the selected period and selection hotel(s) for the specific rate plan',
    },
    {
      'Agency Name':
        'The top 10 travel agencies that had brought in reservations for the selected period and selection hotel(s)',
      'Sel. RN':
        'The room nights observed during the selected period and selection hotel(s) for the specific travel agency',
      'Comp. RN':
        'The room nights observed during the selected period and comparison hotel(s) for the specific travel agency',
      'Var.':
        'The difference between the room nights observed between the selection and the comparison hotel(s)',
      'Sel. Rev':
        'The total amount of reservation revenue observed during the selected period and selection hotel(s) for the specific travel agency',
      'Comp. Rev':
        'The total amount of reservation revenue observed during the selected period and comparison hotel(s) for the specific travel agency',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selected and the comparison hotel(s)',
    },
    {
      'Rate Category':
        'The top 10 rate categories that had grown the most when comparing the selection hotel(s) with the comparison hotel(s)',
      'Sel. RN':
        'The room nights observed during the selected period and selection hotel(s) for the specific rate categories',
      'Comp. RN':
        'The room nights observed during the selected period and comparison hotel(s) for the specific rate categories',
      'Var.':
        'The difference between the room nights observed between the selection and the comparison hotel(s)',
      'Sel. Rev':
        'The total amount of reservation revenue observed during the selected period and selection hotel(s) for the specific rate category',
      'Comp. Rev':
        'The total amount of reservation revenue observed during the selected period and comparison hotel(s) for the specific rate category',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selected period and the comparison period',
    },
    {
      'Rate Category':
        'The worst 10 rate categories that had performed the worst when comparing the selection hotel(s) with the comparison hotel(s)',
      'Sel. RN':
        'The room nights observed during the selected period and selection hotel(s) for the specific rate categories',
      'Comp. RN':
        'The room nights observed during the selected period and comparison hotel(s) for the specific rate categories',
      'Var.':
        'The difference between the room nights observed between the selection and the comparison hotel(s)',
      'Sel. Rev':
        'The total amount of reservation revenue observed during the selected period and selection hotel(s) for the specific rate category',
      'Comp. Rev':
        'The total amount of reservation revenue observed during the selected period and comparison hotel(s) for the specific rate category',
      '% Chg':
        'Percentage change in amount of reservation revenue observed between the selected period and the comparison period',
    },
  ],
  'PDH-2': [
    {
      Channel:
        'The specific channels for which reservations were observed for during the max target date month and the next 2 months for the selection hotel(s)',
      'This Year':
        'Indicates the amount of room nights observed for the first month broken down by channel for the selection hotel(s)',
      'Last Year':
        'Indicates the amount of room nights observed for the first month comparison period broken down by channel limiting the bookings to a same time last period basis for the selection hotel(s)',
      YOY: 'Shows the Year over Year room nights performance of the particular channel for the first month and for the selection hotel(s)',
      'LY Month End':
        'Indicates the amount of room nights observed for the first month comparison period without limiting the booking dates for the selection hotel(s)',
      'TY % MTD OTB':
        "Percentage change in number of room nights observed between the 'This Year' and the 'LY Month End' first month metrics for the selection hotel(s)",
      'This Year\u200b':
        'Indicates the amount of room nights observed for the second month shown broken down by channel for the selection hotel(s)',
      'Last Year\u200b':
        'Indicates the amount of room nights observed for the second month comparison period broken down by channel limiting the bookings to a same time last period basis for the selection hotel(s)',
      'YOY\u200b':
        'Shows the Year over Year room nights performance of the particular channel for the second month and for the selection hotel(s)',
      'LY Month End\u200b':
        'Indicates the amount of room nights observed for the second month comparison period without limiting the booking dates for the selection hotel(s)',
      'TY % MTD OTB\u200b':
        "Percentage change in number of room nights observed between the 'This Year' and the 'LY Month End' second month metrics for the selection hotel(s)",
      'This Year\u200b\u200b':
        'Indicates the amount of room nights observed for the third month shown broken down by channel for the selection hotel(s)',
      'Last Year\u200b\u200b':
        'Indicates the amount of room nights observed for the third month comparison period broken down by channel limiting the bookings to a same time last period basis for the selection hotel(s)',
      'YOY\u200b\u200b':
        'Shows the Year over Year room nights performance of the particular channel for the third month and for the selection hotel(s)',
      'LY Month End\u200b\u200b':
        'Indicates the amount of room nights observed for the third month comparison period without limiting the booking dates for the selection hotel(s)',
      'TY % MTD OTB\u200b\u200b':
        "Percentage change in number of room nights observed between the 'This Year' and the 'LY Month End' third month metrics for the selection hotel(s)",
    },
    {
      firstMonth_title: 'Indicates the first month for which the pace table metrics are calculated',
    },
    {
      secondMonth_title:
        'Indicates the second month for which the pace table metrics are calculated',
    },
    {
      thirdMonth_title: 'Indicates the third month for which the pace table metrics are calculated',
    },
    {
      this_year_label: 'A label showing the max booking date for which the metrics are calculated',
    },
    {
      last_year_label:
        'A label showing the max comparison booking date for which the metrics are calculated',
    },
    {
      this_year_label: 'A label showing the max booking date for which the metrics are calculated',
    },
    {
      last_year_label:
        'A label showing the max comparison booking date for which the metrics are calculated',
    },
    {
      this_year_label: 'A label showing the max booking date for which the metrics are calculated',
    },
    {
      last_year_label:
        'A label showing the max comparison booking date for which the metrics are calculated',
    },
    {
      title_pace_table: 'A label indicating the name of the visual',
    },
  ],

  'TVHF-1,TVH-1': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric: 'Indicates the total revenue generated for the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHF-2,TVH-2': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the total number of occupied rooms for the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVH-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric: 'Indicates the average daily rate for the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVH-4': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the average booking window, in days, for the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVH-5': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric: 'Indicates the average length of stay for the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVH-6': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the number of cancelled reservations for the selection and comparison hotel(s). Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],

  'WTVHF-1,WTVH-1': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the total revenue generated for the selection hotel(s), comparison hotel(s) and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVHF-2,WTVH-2': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the total number of rooms occupied for the selection hotel(s), comparison hotel(s) and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVH-3': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the average daily rate for the selection hotel(s), comparison hotel(s) and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVH-4': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the average booking window, in days, for the selection hotel(s), comparison hotel(s) and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVH-5': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the average length of stay for the selection hotel(s), comparison hotel(s) and day of week',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVH-6': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the number of cancelled reservations for the selection hotel(s), comparison hotel(s) and day of week. Addition of the cancellation count across the day-of-week aggregated values does not represent the total cancellation count for the entire period.',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'CTVHF-1,CTVH-1': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the cumulative total revenue generated for the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'CTVHF-2,CTVH-2': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the cumulative number of occupied rooms for the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'CTVH-4': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the cumulative number of cancelled reservations for the selection and comparison hotel(s). Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHMA-1': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the moving average of total revenue for the selection and comparison hotel(s), calculated over the past n days based on the user selected moving average window',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHMA-2': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the moving average of number of occupied rooms for the selection and comparison hotel(s), calculated over the past n days based on the user selected moving average window',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHMA-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the moving average of the average daily rate for the selection and comparison hotel(s), calculated over the past n days based on the user selected moving average window',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHFP-1': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the percentage change in moving average total revenue for the selection and comparison hotel(s) compared to the baseline period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHFP-2': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the percentage change in moving average number of occupied rooms for the selection and comparison hotel(s) compared to the baseline period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHP-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'Indicates the label for the selection hotel(s) and comparison hotel(s) line in the visuals',
      Metric:
        'Indicates the percentage change in moving average Average Daily Rate (ADR) for the selection and comparison hotel(s) compared to the baseline period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'CTH-1': [
    {
      Date: 'Indicates a stay date from the period selected',
      'A Actls': 'Indicates the actual metric value for the selection hotel(s) and stay date',
      'B Actls': 'Indicates the actual metric value for the comparison hotel(s) and stay date',
      '% Chg':
        'Indicates the percentage difference between the selection and comparison hotel(s) actual metrics',
      'A Fcsts': 'Indicates the forecasted metric value for the selection hotel(s) and stay date',
      'B Fcsts': 'Indicates the forecasted metric value for the comparison hotel(s) and stay date',
      Metric: 'The name of the metric that is calculated for the selection and comparison hotel(s)',
    },
  ],
  'CWTH-1': [
    {
      'Day Name': 'Indicates the day of the week shown',
      'A Actls':
        'Indicates the actual metric value for the selection hotel(s) and stay day of week',
      'B Actls':
        'Indicates the actual metric value for the comparison hotel(s) and stay day of week',
      '% Chg':
        'Indicates the percentage difference between the selection and comparison hotel(s) actual metrics',
      'A Fcsts':
        'Indicates the forecasted metric value for the selection hotel(s) and stay day of week',
      'B Fcsts':
        'Indicates the forecasted metric value for the comparison hotel(s) and stay day of week',
      Metric: 'The name of the metric that is calculated for the selection and comparison hotel(s)',
    },
  ],
  'CCTH-1': [
    {
      Date: 'Indicates a stay date from the period selected',
      'A Actuals':
        'Indicates the cumulative actual metric value for the selection hotel(s) and stay date',
      'B Actuals':
        'Indicates the cumulative actual metric value for the comparison hotel(s) and stay date',
      '% Change':
        'Indicates the percentage difference between the selection and comparison hotel(s) actual metrics',
      'A Forecasts':
        'Indicates the cumulative forecasted metric value for the selection hotel(s) and stay date',
      'B Forecasts':
        'Indicates the cumulative forecasted metric value for the comparison hotel(s) and stay date',
      Metric: 'The name of the metric that is calculated for the selection and comparison hotel(s)',
    },
  ],
  'CMATH-1': [
    {
      Date: 'Indicates a stay date from the period selected',
      'A Actls':
        'Indicates the metric averaged over the past n days (depending on the moving average filter) observed for the selection hotel(s) and selected stay date',
      'B Actls':
        'Indicates the metric averaged over the past n days (depending on the moving average filter) observed for the comparison hotel(s) and selected stay date',
      Metric: 'The name of the metric that is calculated for the selection and comparison hotel(s)',
    },
  ],
  'CPTH-1': [
    {
      Date: 'Indicates a stay date from the period selected',
      'A Actls':
        'Indicates the percentage change in moving average metric for the selection hotel(s) compared to the baseline period',
      'B Actls':
        'Indicates the percentage change in moving average metric for the comparison hotel(s) compared to the baseline period',
      Metric: 'The name of the metric that is calculated for the selection and comparison hotel(s)',
    },
  ],
  'TVHS-1': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the reservation revenue generated for the selected segment and stay date, for both the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHS-2': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the number of occupied rooms for the selected segment and stay date, for both the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHS-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the Average Daily Rate (ADR) for the selected segment and stay date, for both the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHS-4': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the average length of stay for the selected segment and stay date, for both the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHS-5': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the average booking window for the selected segment and stay date, for both the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'TVHS-6': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the total number of cancelled reservations for the selected segment and stay date, for both the selection and comparison hotel(s). Addition of the cancellation count across the daily aggregated values does not represent the total cancellation count for the entire period',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'WTVHS-1': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the total reservation revenue generated for the selected segment and stay day of week, for both the selection and comparison hotel(s)',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVHS-2': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the number of occupied rooms for the selected segment and stay day of week, for both the selection and comparison hotel(s)',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVHS-3': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the Average Daily Rate (ADR) for the selected segment and stay day of week, for both the selection and comparison hotel(s)',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVHS-4': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the average length of stay for the selected segment and stay day of week, for both the selection and comparison hotel(s)',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVHS-5': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the average booking window for the selected segment and stay day of week, for both the selection and comparison hotel(s)',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'WTVHS-6': [
    {
      'Day Name': 'Indicates the day of the week shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the number of cancelled reservations for the selected segment and stay day of week, for both the selection and comparison hotel(s). Addition of the cancellation count across the day-of-week aggregated values does not represent the total cancellation count for the entire period',
    },
    {
      is_sunday_measure:
        'Indicates whether the day shown corresponds to Sunday and is used internally for weekend highlighting in the chart',
      'Day Name': 'Indicates the day of the week associated with the weekend highlighting measure',
    },
    {
      weekly_label: 'Displays the weekly date range currently applied to the visual',
    },
  ],
  'CTVHS-1': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the cumulative total reservation revenue generated for the selected segment and stay date, for both the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'CTVHS-2': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the cumulative number of occupied rooms for the selected segment and stay date, for both the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'CTVHS-3': [
    {
      Date: 'Indicates the stay date shown on the x-axis',
      Value:
        'The labels mention the metric seen for the selection and comparison hotel(s) along with the associated user selected segment category',
      Metric:
        'Indicates the cumulative number of cancelled reservations for the selected segment and stay date, for both the selection and comparison hotel(s)',
    },
    {
      Date: 'Indicates the stay date shown on the x-axis',
      is_sunday_measure:
        'Indicates whether the stay date falls on a Sunday and is used internally for weekend highlighting in the chart',
    },
  ],
  'CTHS-1': [
    {
      Date: 'Indicates a stay date from the period selected',
      Value: 'Label indicating the specific segment from the user selected breakdown option',
      Metric: 'The name of the metric that is calculated for the selection and comparison hotel(s)',
      Label:
        'Label indicating if the metric shown is for the selection hotel(s) or comparison hotel(s)',
      Values: 'Indicates the metric values for the selected segment, stay date and label',
    },
    {
      SD: 'Indicates the user selected stay date period',
      BD: 'Indicates the user selected booking date period',
    },
  ],
  'CWTHS-1': [
    {
      'Day Name': 'Indicates the day of the week shown',
      Value: 'Label indicating the specific segment from the user selected breakdown option',
      Metric: 'The name of the metric that is calculated for the selection and comparison hotel(s)',
      Label:
        'Label indicating if the metric shown is for the selection hotel(s) or comparison hotel(s)',
      metrics_spot:
        'Indicates the metric values for the selected segment, stay day of week and label',
    },
    {
      SD: 'Indicates the user selected stay date period',
      BD: 'Indicates the user selected booking date period',
    },
  ],
  'CCTHS-1': [
    {
      Date: 'Indicates a stay date from the period selected',
      Value: 'Label indicating the specific segment from the user selected breakdown option',
      Metric: 'The name of the metric that is calculated for the selection and comparison hotel(s)',
      Label:
        'Label indicating if the metric shown is for the selection hotel(s) or comparison hotel(s)',
      Values:
        'Indicates the cumulative metric values for the selected segment, stay day of week and label',
    },
    {
      SD: 'Indicates the user selected stay date period',
      BD: 'Indicates the user selected booking date period',
    },
  ],
  'SDH-1': [
    {
      Value: 'Label indicating if the selection or comparison hotel group metrics are shown',
      Currency: 'The currency for which the reservation revenue is shown',
      Revenue:
        'The amount of reservation revenue observed for the selected segment and selection or comparison hotel group',
    },
    {
      Revenue_title: 'Title for the revenue widget',
    },
    {
      selection_hotel_Revenue_card:
        'Card indicating the reservation revenue observed for the selection hotel group',
    },
    {
      comparison_hotel_Revenue_card:
        'Card indicating the reservation revenue observed for the comparison hotel group',
    },
    {
      hotels_perc_change_all_card:
        'Card indicating the percentage difference in the reservation revenue observed between the selection and comparison hotel group',
    },
    {
      Comparison_label: 'Label indicating the metric above it is for the comparison hotel group',
    },
    {
      REVPAR_selection_card: 'Shows the Revenue Per Available Room for the selection hotel group',
    },
    {
      Value: 'Label indicating if the selection or comparison hotel group metrics are shown',
      TopN_dynamic_date_Rooms_hotels_deagg:
        'The number of occupied rooms observed for the selected segment and selection or comparison hotel group',
    },
    {
      selection_hotel_Rooms_card:
        'Card indicating the number of occupied rooms observed for the selection hotel group',
    },
    {
      comparison_hotel_Rooms_card:
        'Card indicating the number of occupied rooms observed for the comparison hotel group',
    },
    {
      hotels_perc_change_all_card:
        'Card indicating the percentage difference in the number of occupied rooms observed between the selection and comparison hotel group',
    },
    {
      Comparison_label: 'Label indicating the metric above it is for the comparison hotel group',
    },
    {
      OccupancyRate_selection_agg:
        'Card indicating the occupancy rate for the selection hotel group',
    },

    {
      Value: 'Label indicating if the selection or comparison hotel group metrics are shown',
      TopN_dynamic_date_ADR_hotels_deagg:
        'The Average Daily Rate (ADR) observed for the selected segment and selection or comparison hotel group',
    },
    {
      ADR_title: 'Title for the average daily rate widget',
    },
    {
      selection_hotel_ADR_card:
        'Card indicating the Average Daily Rate (ADR) for the selection hotel group',
    },
    {
      comparison_hotel_ADR_card:
        'Card indicating the Average Daily Rate (ADR) for the comparison hotel group',
    },
    {
      hotels_perc_change_all_card:
        'Label indicating the percentage difference in the Average Daily Rate (ADR) observed between the selection and comparison hotel group',
    },
    {
      Comparison_label: 'Label indicating the metric above it is for the comparison hotel group',
    },
  ],
  'SDH-2': [
    {
      Value: 'Label indicating if the selection or comparison hotel group metrics are shown',
      Cancellations:
        'The number of cancelled reservations observed for the selected segment and selection or comparison hotel group',
    },
    {
      selection_hotel_TrxCount_card:
        'Card indicating the number of cancelled reservations for the selection hotel group',
    },
    {
      comparison_hotel_TrxCount_card:
        'Card indicating the number of cancelled reservations for the comparison hotel group',
    },
    {
      hotels_perc_change_all_card:
        'The percentage in cancelled reservations observed between the selection and comparison hotel groups',
    },
    {
      Comparison_label: 'Label indicating the metric above it is for the comparison hotel group',
    },
    {
      Value: 'Label indicating if the selection or comparison hotel group metrics are shown',
      Reservations:
        'The number of confirmed reservations observed for the selected segment and selection or comparison hotel group',
    },
    {
      selection_hotel_TrxCount_card:
        'Card indicating the number of confirmed reservations for the selection hotel group',
    },
    {
      comparison_hotel_TrxCount_card:
        'Card indicating the number of confirmed reservations for the comparison hotel group',
    },
    {
      hotels_perc_change_all_card:
        'The percentage in confirmed reservations observed between the selection and comparison hotel groups',
    },
    {
      Comparison_label: 'Label indicating the metric above it is for the comparison hotel group',
    },
    {
      Currency: 'Currency for which the revenue metrics are shown',
      'Total Revenue': 'The total revenue observed for the selection group of hotels',
      'Channel (raw)': 'The segment for which the total reservation is shown for',
    },
    {
      selection_hotel_bookingWindow:
        'The average booking window observed for the selection group of hotels',
    },
    {
      selection_hotel_LoS: 'The average length of stay observed for the selection group of hotels',
    },
  ],
  'SDH-3': [
    {
      'Booking Date': 'The particular booking date for the selected target date',
      HotelName: 'The labels mention the metric seen for the selection and comparison hotel(s)',
      pace_variable_hotel_comparison:
        'Indicates the number of occupied rooms or the reservation revenue generated for the selection and comparison hotel(s) depending on the pace variable selected',
    },
  ],
  'CTT-1': [
    {
      Date: 'Indicates a particular stay date from the user selected date period',
      'Select.': 'Indicates the metric value observed for the selected stay date',
      'Compar.':
        'Indicates the metric value observed for the equivalent comparison stay date based on the selected time comparison period',
      '% Chg': 'Indicates the percentage difference between the selection and comparison values',
      Forecast:
        'Indicates the forecasted reservation revenue or occupied rooms predeicted for future stay dates',
      Metric: 'Indicates the metric for which values are calculated for',
    },
    {
      SD: 'A label indicating the user-defined stay date range',
      BD: 'A label indicating the user-defined booking date range',
      Header_currency:
        'A label indicating the currency for which the reservation revenue and ADR metrics are shown',
      Header_pms:
        'A label indicating if PMS transactions were included in the data shown in the tabular visual',
    },
    {
      'Comp. SD': 'A label indicating the comparison stay date period',
      'Comp. BD': 'A label indicating the comparison booking date period',
      'Comp.': 'Indicates the time comparison period',
    },
  ],
  'CCTT-1': [
    {
      Date: 'Indicates a particular stay date from the user selected date period',
      'Select.': 'Indicates the cumulative metric value observed for the selected stay date',
      'Compar.':
        'Indicates the cumulative metric value observed for the equivalent comparison stay date based on the selected time comparison period',
      '% Chg': 'Indicates the percentage difference between the selection and comparison values',
      Forecast:
        'Indicates the cumulative forecasted reservation revenue or occupied rooms predicted for future stay dates',
      Metric: 'Indicates the metric for which values are calculated for',
    },

    {
      SD: 'A label indicating the user-defined stay date range',
      BD: 'A label indicating the user-defined booking date range',
      Header_currency:
        'A label indicating the currency for which the reservation revenue and ADR metrics are shown',
    },

    {
      'Comp. SD': 'A label indicating the comparison stay date period',
      'Comp. BD': 'A label indicating the comparison booking date period',
      'Comp.': 'Indicates the time comparison period',
    },
  ],
  'CWTT-1': [
    {
      SD: 'A label indicating the user-defined stay date range',
      BD: 'A label indicating the user-defined booking date range',
      Currency:
        'A label indicating the currency for which the reservation revenue and ADR metrics are shown',
    },
    {
      'Comp. SD': 'A label indicating the comparison stay date period',
      'Comp. BD': 'A label indicating the comparison booking date period',
      'Comp.': 'Indicates the time comparison period',
    },
    {
      'Day Name': 'Indicates a particular stay day of week from the user selected date period',
      'Select.': 'Indicates the metric value observed for the selected stay day',
      'Compar.':
        'Indicates the metric value observed for the equivalent comparison stay day based on the selected time comparison period',
      '% Chg': 'Indicates the percentage difference between the selection and comparison values',
      Forecast:
        'Indicates the forecasted reservation revenue or occupied rooms predeicted for future stay day',
      Metric: 'Indicates the metric for which values are calculated for',
    },
  ],
  'CMATT-1': [
    {
      Date: 'Indicates a particular stay date from the user selected date period',
      Metric: 'Indicates the metric for which values are calculated for',
      Selection:
        'Indicates the metric averaged over the past n days (depending on the moving average filter) observed for the selected stay date',
      Comparison:
        'Indicates the metric averaged over the past n days (depending on the moving average filter) observed for the equivalent comparison stay date based on the selected time comparison period',
    },

    {
      SD: 'A label indicating the user-defined stay date range',
      BD: 'A label indicating the user-defined booking date range',
      Header_currency:
        'A label indicating the currency for which the reservation revenue and ADR metrics are shown',
    },
    {
      'Comp. SD': 'A label indicating the comparison stay date period',
      'Comp. BD': 'A label indicating the comparison booking date period',
      'Comp.': 'Indicates the time comparison period',
    },
  ],

  'CPTT-1': [
    {
      Date: 'Indicates a particular stay date from the user selected date period',
      Selection:
        'Indicates the percentage difference between the first moving average value observed in the stay date range and the moving average values observed for the selected stay date',
      Comparison:
        'Indicates the percentage difference between the first moving average value observed in the comparison stay date range and the moving average values observed for the equivalent comparison stay date based on the selected time comparison period',
      Metric: 'Indicates the metric for which values are calculated for',
    },

    {
      SD: 'A label indicating the user-defined stay date range',
      BD: 'A label indicating the user-defined booking date range',
    },

    {
      'Comp. SD': 'A label indicating the comparison stay date period',
      'Comp. BD': 'A label indicating the comparison booking date period',
      'Comp.': 'Indicates the time comparison period',
    },
  ],
};

export default columnSchema;
